import { useEffect, useState } from "react";
import { LogOut, Package, Mail, Phone, Eye, EyeOff, MapPin, Pencil, X, Store, UserRound, CalendarCheck, Ban, Share2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError, ORDER_STATUS } from "@/lib/api";
import { shareOrCopy } from "@/lib/share";
import { PICKUP_SLOTS } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";
import { DocumentLinks } from "@/pages/Admin";
import { ClientMessenger } from "@/components/Messaging";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

const inputCls =
  "w-full bg-white border border-[#6B1226]/15 rounded-xl px-4 py-3 text-base outline-none focus:border-[#6B1226]/50 transition-colors";

export default function Profile() {
  const { user, loading, login, register, logout, setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", phone: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState(null);
  const [reservations, setReservations] = useState(null);

  const loadReservations = () => api.get("/reservations/mine").then((res) => setReservations(res.data)).catch(() => setReservations([]));
  const [editing, setEditing] = useState(false);
  const [pform, setPform] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (user) {
      api.get("/orders/mine").then((res) => setOrders(res.data)).catch(() => setOrders([]));
      loadReservations();
    }
  }, [user]);

  const cancelReservation = async (r) => {
    try {
      await api.post(`/reservations/${r.id}/cancel`);
      toast.success("Réservation annulée — à une prochaine fois");
      loadReservations();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Ravi de vous revoir ${u.first_name || u.name}`);
      } else {
        const u = await register({ first_name: form.firstName, last_name: form.lastName, email: form.email, password: form.password, phone: form.phone || undefined });
        toast.success(`Bienvenue ${u.first_name || u.name} au Bistroquet`);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setPform({
      firstName: user.first_name || "",
      lastName: user.last_name || "",
      email: user.email || "",
      phone: user.phone || "",
      address1: user.address_line1 || "",
      address2: user.address_line2 || "",
      postalCode: user.postal_code || "",
      city: user.city || "",
      slot: user.preferred_slot || "",
      note: user.preferred_note || "",
      birthDate: user.birth_date || "",
    });
    setSaveError("");
    setEditing(true);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError("");
    try {
      const res = await api.put("/auth/profile", {
        first_name: pform.firstName,
        last_name: pform.lastName,
        email: pform.email,
        phone: pform.phone || undefined,
        address_line1: pform.address1 || undefined,
        address_line2: pform.address2 || undefined,
        postal_code: pform.postalCode || undefined,
        city: pform.city || undefined,
        preferred_slot: pform.slot || undefined,
        preferred_note: pform.note || undefined,
        birth_date: pform.birthDate || undefined,
      });
      setUser(res.data);
      setEditing(false);
      toast.success("Profil mis à jour");
    } catch (err) {
      setSaveError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-32 text-center" data-testid="profile-loading">
        <p className="font-display text-2xl text-[#6B1226] italic">Un instant…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="profile-page">
        <section className="app-container px-4 py-14 sm:py-20">
          <Reveal>
            <div className="text-center mb-8">
              <p className="eyebrow mb-3">Votre compte</p>
              <h1 className="font-display text-4xl font-semibold text-[#1C1917]">
                {mode === "login" ? "Bon retour" : "Rejoignez la table"}
              </h1>
              <p className="text-sm text-[#57534E] mt-2">
                Suivez vos commandes d'enlèvement et téléchargez vos documents.
              </p>
            </div>
            <form onSubmit={submit} className="bg-white rounded-3xl border border-[#6B1226]/10 card-shadow p-6 sm:p-8 space-y-3.5" data-testid="auth-form">
              {mode === "register" && (
                <>
                  <div className="grid grid-cols-2 gap-2.5">
                    <input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="Prénom" className={inputCls} data-testid="auth-firstname-input" />
                    <input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Nom" className={inputCls} data-testid="auth-lastname-input" />
                  </div>
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone mobile (facultatif)" className={inputCls} data-testid="auth-phone-input" />
                </>
              )}
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="auth-email-input" />
              <div className="relative">
                <input required type={showPassword ? "text" : "password"} minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe (6 caractères min.)" className={`${inputCls} pr-11`} data-testid="auth-password-input" />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B1226]/50 hover:text-[#6B1226] transition-colors"
                  data-testid="auth-password-toggle"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              {error && (
                <p className="text-sm text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-4 py-3" data-testid="auth-error">
                  {error}
                </p>
              )}
              <button type="submit" disabled={busy} className="w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-3.5 text-sm font-bold tracking-wide hover:bg-[#580E1D] transition-colors disabled:opacity-60" data-testid="auth-submit-button">
                {busy ? "Un instant…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setError("");
                }}
                className="w-full text-center text-xs text-[#6B1226] font-semibold hover:underline pt-1"
                data-testid="auth-toggle-mode"
              >
                {mode === "login" ? "Pas encore de compte ? Créez-le" : "Déjà des nôtres ? Connectez-vous"}
              </button>
            </form>
          </Reveal>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="profile-page">
      <section className="app-container px-4 sm:px-8 py-12 sm:py-16">
        <Reveal>
          <div className="bg-white rounded-3xl border border-[#6B1226]/10 card-shadow p-6 sm:p-8" data-testid="profile-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="w-14 h-14 rounded-full bg-[#6B1226] text-[#FAF6F0] flex items-center justify-center font-display text-2xl font-semibold">
                  {(user.first_name || user.name)?.charAt(0)?.toUpperCase() || "B"}
                </span>
                <div data-testid="profile-user-info">
                  <p className="eyebrow">Bonjour {user.first_name || user.name}</p>
                  <h1 className="font-display text-3xl font-semibold text-[#1C1917]">{user.name}</h1>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2">
                {!editing && (
                  <button
                    onClick={startEdit}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B1226] border border-[#6B1226]/25 rounded-full px-4 py-2 hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors"
                    data-testid="profile-edit-button"
                  >
                    <Pencil size={13} /> Modifier
                  </button>
                )}
                <button
                  onClick={() => logout().then(() => toast.success("À bientôt"))}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B1226] border border-[#6B1226]/25 rounded-full px-4 py-2 hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors"
                  data-testid="profile-logout-button"
                >
                  <LogOut size={13} /> Sortir
                </button>
              </div>
            </div>

            {!editing ? (
              <div className="mt-5 space-y-2 text-sm text-[#57534E]" data-testid="profile-details">
                <p className="flex items-center gap-2"><Mail size={14} className="text-[#6B1226]" /> {user.email}</p>
                {user.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-[#6B1226]" /> {user.phone}</p>}
                {user.address_line1 ? (
                  <p className="flex items-start gap-2" data-testid="profile-address">
                    <MapPin size={14} className="text-[#6B1226] mt-0.5 shrink-0" />
                    <span>
                      {user.address_line1}
                      {user.address_line2 ? `, ${user.address_line2}` : ""}
                      {(user.postal_code || user.city) && (
                        <>
                          <br />
                          {[user.postal_code, user.city].filter(Boolean).join(" ")}
                        </>
                      )}
                    </span>
                  </p>
                ) : (
                  <p className="flex items-center gap-2 italic text-[#A8A29E]">
                    <MapPin size={14} /> Adresse non renseignée — ajoutez-la via « Modifier »
                  </p>
                )}
                {user.preferred_slot && (
                  <p className="flex items-center gap-2" data-testid="profile-preferred-slot">
                    <Store size={14} className="text-[#6B1226]" /> Retrait préféré :{" "}
                    {/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(user.preferred_slot)
                      ? (() => {
                          const d = new Date(user.preferred_slot);
                          const jour = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
                          return `${jour.charAt(0).toUpperCase() + jour.slice(1)} à ${user.preferred_slot.slice(11).replace(":", "h")}`;
                        })()
                      : user.preferred_slot}
                  </p>
                )}
                {user.birth_date && (
                  <p className="flex items-center gap-2 text-[#57534E]" data-testid="profile-birthdate">
                    <UserRound size={14} className="text-[#6B1226]" /> Né(e) le {new Date(user.birth_date + "T00:00:00").toLocaleDateString("fr-FR")}
                  </p>
                )}
              </div>
            ) : (
              <form onSubmit={saveProfile} className="mt-6 space-y-3" data-testid="profile-edit-form">
                <div className="grid grid-cols-2 gap-2.5">
                  <input required value={pform.firstName} onChange={(e) => setPform({ ...pform, firstName: e.target.value })} placeholder="Prénom" className={inputCls} data-testid="profile-edit-firstname" />
                  <input required value={pform.lastName} onChange={(e) => setPform({ ...pform, lastName: e.target.value })} placeholder="Nom" className={inputCls} data-testid="profile-edit-lastname" />
                </div>
                <input required type="email" value={pform.email} onChange={(e) => setPform({ ...pform, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="profile-edit-email" />
                <input type="tel" value={pform.phone} onChange={(e) => setPform({ ...pform, phone: e.target.value })} placeholder="Téléphone mobile" className={inputCls} data-testid="profile-edit-phone" />
                <input value={pform.address1} onChange={(e) => setPform({ ...pform, address1: e.target.value })} placeholder="Adresse complète (n°, rue, bâtiment…)" className={inputCls} data-testid="profile-edit-address1" />
                <input value={pform.address2} onChange={(e) => setPform({ ...pform, address2: e.target.value })} placeholder="Complément d'adresse (facultatif)" className={inputCls} data-testid="profile-edit-address2" />
                <div className="grid grid-cols-[6rem_1fr] gap-2.5">
                  <input inputMode="numeric" pattern="[0-9]{5}" maxLength={5} value={pform.postalCode} onChange={(e) => setPform({ ...pform, postalCode: e.target.value })} placeholder="Code postal" className={inputCls} data-testid="profile-edit-postal" />
                  <input value={pform.city} onChange={(e) => setPform({ ...pform, city: e.target.value })} placeholder="Ville" className={inputCls} data-testid="profile-edit-city" />
                </div>
                <p className="eyebrow pt-2">Préférences d'enlèvement (imprimées sur vos bons, jamais sur la facture)</p>
                <select value={pform.slot} onChange={(e) => setPform({ ...pform, slot: e.target.value })} className={inputCls} data-testid="profile-edit-slot">
                  <option value="">Créneau de retrait préféré (facultatif)</option>
                  {PICKUP_SLOTS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <input value={pform.note} onChange={(e) => setPform({ ...pform, note: e.target.value })} placeholder="Note habituelle pour le sommelier (facultatif)" className={inputCls} data-testid="profile-edit-note" />
                <div>
                  <label className="block text-xs font-semibold text-[#57534E] mb-1">Date de naissance</label>
                  <input type="date" value={pform.birthDate} onChange={(e) => setPform({ ...pform, birthDate: e.target.value })} max={new Date().toISOString().slice(0, 10)} className={inputCls} data-testid="profile-edit-birthdate" />
                </div>
                {saveError && (
                  <p className="text-sm text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-4 py-3" data-testid="profile-edit-error">
                    {saveError}
                  </p>
                )}
                <div className="flex gap-2.5 pt-1">
                  <button type="submit" disabled={saving} className="flex-1 rounded-full bg-[#6B1226] text-[#FAF6F0] py-3 text-sm font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60" data-testid="profile-save-button">
                    {saving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#6B1226]/25 text-[#6B1226] px-5 py-3 text-sm font-semibold hover:bg-[#6B1226]/5 transition-colors"
                    data-testid="profile-cancel-button"
                  >
                    <X size={14} /> Annuler
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.05} className="mt-10">
          <h2 className="font-display text-2xl font-semibold text-[#1C1917] flex items-center gap-2.5">
            <CalendarCheck size={20} className="text-[#6B1226]" /> Mes réservations de table
          </h2>
          {reservations === null ? (
            <p className="mt-6 text-sm text-[#57534E] italic">Chargement…</p>
          ) : (
            (() => {
              const today = new Date().toLocaleDateString("sv-SE");
              const upcoming = reservations.filter((r) => r.status !== "annulee" && r.status !== "honoree" && r.date >= today);
              const past = reservations.filter((r) => r.status !== "annulee" && (r.status === "honoree" || r.date < today));
              const cancelled = reservations.filter((r) => r.status === "annulee");
              if (upcoming.length === 0 && past.length === 0 && cancelled.length === 0)
                return (
                  <div className="mt-6 bg-white rounded-2xl border border-[#6B1226]/10 p-8 text-center" data-testid="profile-reservations-empty">
                    <p className="font-display text-xl text-[#1C1917]">Aucune réservation pour l'instant</p>
                    <p className="text-sm text-[#57534E] mt-1.5">Réservez votre table en 30 secondes, sans appeler.</p>
                  </div>
                );
              return (
                <>
                  {upcoming.length > 0 && (
                    <ul className="mt-6 space-y-3" data-testid="profile-reservations-list">
                      {upcoming.map((r, i) => (
                        <li key={r.id} className="bg-white rounded-2xl border border-[#6B1226]/10 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`profile-reservation-${i}`}>
                          <div className="flex items-center gap-4">
                            <div className="text-center shrink-0">
                              <p className="font-display text-xl font-semibold text-[#6B1226] leading-none">{r.time.replace(":", "h")}</p>
                              <p className="text-[0.6rem] font-eyebrow tracking-[0.14em] uppercase text-[#A8A29E] mt-1">{r.service}</p>
                            </div>
                            <span className="self-stretch w-px bg-[#6B1226]/10" />
                            <div>
                              <p className="text-sm font-semibold text-[#1C1917]">
                                {(() => {
                                  const d = new Date(r.date + "T12:00:00");
                                  const s = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
                                  return s.charAt(0).toUpperCase() + s.slice(1);
                                })()}
                              </p>
                              <p className="text-xs text-[#57534E] mt-0.5">
                                {r.guests} couvert{r.guests > 1 ? "s" : ""} ·{" "}
                                <span className={r.status === "confirmee" ? "text-[#2D5A27] font-semibold" : "text-[#8C6D23] font-semibold"}>
                                  {r.status === "confirmee" ? "Confirmée par le restaurant" : "En attente de confirmation"}
                                </span>
                              </p>
                              {r.note && <p className="text-xs italic text-[#A8A29E] mt-0.5">« {r.note} »</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                shareOrCopy({
                                  title: "Table réservée — Le Bistroquet, Belfort",
                                  text: `J'ai réservé une table pour ${r.guests} au Bistroquet (Belfort) — le ${r.date.split("-").reverse().join("/")} à ${r.time.replace(":", "h")}. Rejoins-nous !`,
                                  url: `${window.location.origin}/api/share/reservation/${r.id}`,
                                  image: `${window.location.origin}/api/share/reservation/${r.id}/card.jpg`,
                                  copiedMsg: "Lien de réservation copié — partagez-le à vos convives",
                                })
                              }
                              className="inline-flex items-center gap-1.5 rounded-full border border-[#8C6D23]/30 text-[#8C6D23] px-4 py-2 text-xs font-semibold hover:bg-[#8C6D23] hover:text-[#FAF6F0] transition-colors"
                              data-testid={`profile-reservation-share-${i}`}
                              aria-label="Partager cette réservation"
                            >
                              <Share2 size={12} /> Partager
                            </button>
                            <button
                              onClick={() => cancelReservation(r)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-[#6B1226]/25 text-[#6B1226] px-4 py-2 text-xs font-semibold hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors"
                              data-testid={`profile-reservation-cancel-${i}`}
                            >
                              <Ban size={12} /> Annuler
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {past.length > 0 && (
                    <div className="mt-8" data-testid="profile-reservations-past">
                      <p className="font-eyebrow text-[0.7rem] font-semibold tracking-[0.18em] uppercase text-[#8C6D23]">Déjà effectuées</p>
                      <p className="mt-2 text-sm text-[#57534E] leading-relaxed max-w-xl">
                        Un immense merci pour votre visite au Bistroquet ! Si le moment vous a plu, quelques étoiles et un petit mot
                        sur Google sont le plus beau cadeau que vous puissiez nous faire — cela nous aide énormément.
                      </p>
                      <ul className="mt-3 space-y-2.5">
                        {past.map((r, i) => (
                          <li key={r.id} className="bg-white/70 rounded-2xl border border-[#C5A059]/25 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`profile-reservation-past-${i}`}>
                            <p className="text-sm text-[#57534E]">
                              <span className="font-semibold text-[#1C1917]">
                                {(() => {
                                  const d = new Date(r.date + "T12:00:00");
                                  const s = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
                                  return s.charAt(0).toUpperCase() + s.slice(1);
                                })()}
                              </span>{" "}
                              à {r.time.replace(":", "h")} · {r.guests} couvert{r.guests > 1 ? "s" : ""}
                            </p>
                            <a
                              href="https://www.google.com/search?q=avis+sur+le+bistroquet+belfort&hl=fr"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 rounded-full border border-[#8C6D23]/30 text-[#8C6D23] px-4 py-2 text-xs font-semibold hover:bg-[#8C6D23] hover:text-[#FAF6F0] transition-colors"
                              data-testid={`profile-reservation-review-${i}`}
                            >
                              <Star size={12} /> Noter le restaurant sur Google
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {cancelled.length > 0 && (
                    <div className="mt-8" data-testid="profile-reservations-cancelled">
                      <p className="font-eyebrow text-[0.7rem] font-semibold tracking-[0.18em] uppercase text-[#A8A29E]">Annulées</p>
                      <ul className="mt-3 space-y-2.5">
                        {cancelled.map((r, i) => (
                          <li key={r.id} className="rounded-2xl border border-dashed border-[#A8A29E]/40 px-4 py-3 flex items-center justify-between gap-3 opacity-70" data-testid={`profile-reservation-cancelled-${i}`}>
                            <p className="text-sm text-[#57534E] line-through">
                              {(() => {
                                const d = new Date(r.date + "T12:00:00");
                                const s = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
                                return s.charAt(0).toUpperCase() + s.slice(1);
                              })()}{" "}
                              à {r.time.replace(":", "h")} · {r.guests} couvert{r.guests > 1 ? "s" : ""}
                            </p>
                            <span className="shrink-0 rounded-full bg-[#6B1226]/10 text-[#6B1226] px-3 py-1 text-[11px] font-semibold no-underline">Annulée</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              );
            })()
          )}
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <h2 className="font-display text-2xl font-semibold text-[#1C1917] flex items-center gap-2.5">
            <Package size={20} className="text-[#6B1226]" /> Mes commandes d'enlèvement
          </h2>
          {orders === null ? (
            <p className="mt-6 text-sm text-[#57534E] italic">Chargement…</p>
          ) : orders.length === 0 ? (
            <div className="mt-6 bg-white rounded-2xl border border-[#6B1226]/10 p-8 text-center" data-testid="profile-orders-empty">
              <p className="font-display text-xl text-[#1C1917]">Aucune commande pour l'instant</p>
              <p className="text-sm text-[#57534E] mt-1.5">Vos futures bouteilles apparaîtront ici.</p>
            </div>
          ) : (
            <ul className="mt-6 space-y-4" data-testid="profile-orders-list">
              {orders.map((order) => {
                const status = ORDER_STATUS[order.status] || ORDER_STATUS.nouvelle;
                return (
                  <li key={order.id} className="bg-white rounded-2xl border border-[#6B1226]/10 p-5" data-testid="profile-order-card">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-eyebrow text-xs tracking-[0.25em] text-[#6B1226]">N° {order.number}</p>
                      <span className={`rounded-full px-3 py-1 text-[0.65rem] font-semibold tracking-wide ${status.classes}`} data-testid="profile-order-status">
                        {status.label}
                      </span>
                    </div>
                    <ul className="mt-3 text-sm text-[#57534E] space-y-1">
                      {order.items.map((item, i) => (
                        <li key={i} className="flex justify-between gap-3">
                          <span>{item.qty} × {item.name}</span>
                          <span className="price text-[#1C1917]">{eur(item.line_total)}</span>
                        </li>
                      ))}
                    </ul>
                    {order.discount_total > 0 && (
                      <p className="mt-2 text-xs text-[#8C6D23]">Remise panachée appliquée : −{eur(order.discount_total)}</p>
                    )}
                    <div className="mt-3 pt-3 border-t border-[#6B1226]/10 flex flex-wrap justify-between gap-2 text-sm">
                      <span className="text-[#78716C] text-xs">{order.pickup_time}</span>
                      <span className="price text-[#6B1226]">{eur(order.total)}</span>
                    </div>
                    {order.paid && (
                      <p className="mt-2 text-[0.65rem] font-semibold text-[#3F6212]" data-testid="profile-order-paid">
                        Facture {order.invoice_number} — acquittée ({order.payment_method})
                      </p>
                    )}
                    <DocumentLinks order={order} />
                  </li>
                );
              })}
            </ul>
          )}
        </Reveal>

        <Reveal delay={0.15} className="mt-10">
          <ClientMessenger />
        </Reveal>
      </section>
      <Footer />
    </div>
  );
}
